import { Form } from '@/components';
import { Flex } from '@/components/Common';
import { MasterTranslation, SignUpSource, theme } from '@/constants';
import { useGlobalState, useHighlightString } from '@/hooks';
import { LoginMethod } from '@/interfaces';
import { Divider } from '@/modules/pmt/components/Divider';
import type { IPmtOnboardingPopup } from '@/modules/pmt/interfaces';
import { getMasterTranslationLanguageCode } from '@/utils';
import { getCurrentTextOfMultipleLanguageField } from '@/utils/text.utils';
import Image from 'next/image';
import { useSignIn } from '../../../../hooks/useSignIn.hook';
import { LoginEsyncButton } from '../../../LoginEsyncButton';
import { PopupContentWrapper, Title } from './PopupContent.styles';

export const PopupContent: React.FC<IPmtOnboardingPopup & { isExpanded: boolean }> = ({
  joinUsDesktopMainText,
  loginByEmail,
  desktopGoogleCta,
  identifier,
  subPmcNewsletterText,
  delayTime,
  zapierHookId,
  isExpanded,
  joinUsValuePropositionList,
}) => {
  const { t, getProperImageUrl } = useGlobalState();
  const { pmtCurrentLanguageCode } = useGlobalState();
  const languageField = getMasterTranslationLanguageCode(pmtCurrentLanguageCode);
  const { handleSuccess, triggerGtmClickJoinUs, EmailField, cta } = useSignIn({
    identifier,
    subPmcNewsletterText,
    delayTime,
    zapierHookId,
  });

  const firstPropositionItem = joinUsValuePropositionList?.[0];
  const firstPropositionItemTitle = getCurrentTextOfMultipleLanguageField(firstPropositionItem?.title, languageField);
  const title = isExpanded ? joinUsDesktopMainText : firstPropositionItemTitle;
  const highlightCss = {
    color: theme.colors.darkOrange,
  };
  const highlightedTitle = useHighlightString(title, highlightCss, 'span');

  return (
    <PopupContentWrapper isExpanded={isExpanded}>
      <Flex align='center'>
        {!isExpanded && firstPropositionItem?.icon?.url && (
          <Image
            width={48}
            height={48}
            src={getProperImageUrl(firstPropositionItem.icon)}
            alt={firstPropositionItemTitle || 'Proposition icon'}
            style={{ marginRight: 8 }}
          />
        )}
        {highlightedTitle && <Title isExpanded={isExpanded}>{highlightedTitle}</Title>}
      </Flex>

      {isExpanded && (
        <LoginEsyncButton
          googleCta={desktopGoogleCta}
          handleClick={() => triggerGtmClickJoinUs(LoginMethod.Google)}
          handleSuccess={handleSuccess}
        />
      )}

      {!!loginByEmail && isExpanded && (
        <>
          <Divider>{t(MasterTranslation.TrackManually)}</Divider>

          {/* Email */}
          <Form
            formFieldList={[EmailField]}
            buttonText={t(MasterTranslation.Continue)}
            submittingButtonText={t(MasterTranslation.Submitting)}
            promptBoxContent={t(MasterTranslation.ExistEmailPromptBox)}
            signUpSource={SignUpSource.ConsumerWeb}
            isEmailForm
            isPMTButton
            truncate
            onSuccess={() => triggerGtmClickJoinUs(LoginMethod.Email)}
            cta={cta}
          />
        </>
      )}
    </PopupContentWrapper>
  );
};
